import { useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useStores } from 'Hooks/useStore';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import { GET_GROUPS_LIST } from 'Services/Api/Groups/Queries';
import {
	GetGroupsListRequest,
	GetGroupsListResponse,
	Group
} from 'Services/Api/Groups/Types';
import { DashboardFiltersValues } from 'Pages/Dashboard/Dashboard.types';

export interface PropertiesFilterProps {
	onChange: (values: DashboardFiltersValues) => void;
}

export function usePropertiesFilter({ onChange }: PropertiesFilterProps) {
	const { authStore, systemsStore } = useStores();
	const isSuperAdmin =
		authStore.currentUser?.primarySystemRole?.role ===
		SystemRoleType.SuperAdmin;

	const [open, setOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [tenants, setTenants] = useState<Group[]>([]);
	const [propertiesByParent, setPropertiesByParent] = useState<
		Record<number, Group[]>
	>({});
	const [expandedTenants, setExpandedTenants] = useState<Set<number>>(
		new Set()
	);
	const [loadingRoot, setLoadingRoot] = useState(false);
	const [loadingParentId, setLoadingParentId] = useState<number | null>(null);

	const client = useApolloClient();

	const loadGroups = useCallback(
		async (parentId: number | null) => {
			if (parentId === null) setLoadingRoot(true);
			else setLoadingParentId(parentId);
			try {
				const result = await client.query<
					GetGroupsListResponse,
					GetGroupsListRequest
				>({
					query: GET_GROUPS_LIST,
					fetchPolicy: 'no-cache',
					variables: { sort: 'name:asc', filters: { parentId } }
				});
				const groups = result.data?.listGroups ?? [];
				if (parentId === null) {
					setTenants(groups);
				} else {
					setPropertiesByParent(prev => ({ ...prev, [parentId]: groups }));
				}
			} finally {
				if (parentId === null) setLoadingRoot(false);
				else setLoadingParentId(null);
			}
		},
		[client]
	);

	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			setOpen(nextOpen);
			if (!nextOpen) return;
			if (isSuperAdmin) {
				if (tenants.length === 0 && !loadingRoot) loadGroups(null);
			} else {
				const tenantId = systemsStore.selectedTenant.value;
				if (tenantId && !propertiesByParent[tenantId] && loadingParentId !== tenantId) {
					loadGroups(tenantId);
				}
			}
		},
		[
			isSuperAdmin,
			tenants.length,
			loadingRoot,
			propertiesByParent,
			loadingParentId,
			loadGroups,
			systemsStore
		]
	);

	const toggleTenant = useCallback(
		(id: number) => {
			setExpandedTenants(prev => {
				const next = new Set(prev);
				if (next.has(id)) {
					next.delete(id);
				} else {
					next.add(id);
					if (!propertiesByParent[id] && loadingParentId !== id) {
						loadGroups(id);
					}
				}
				return next;
			});
		},
		[propertiesByParent, loadingParentId, loadGroups]
	);

	const toggleCheck = useCallback(
		(id: number) => {
			setSelectedIds(prev => {
				let next: number[];

				if (isSuperAdmin) {
					// multi-select for SuperAdmin
					next = prev.includes(id)
						? prev.filter(i => i !== id)
						: [...prev, id];
				} else {
					// single-select for admin/users
					next = prev.includes(id) ? [] : [id];
				}

				if (next.length === 0) {
					onChange({});
				} else if (next.length === 1) {
					onChange({ id: next[0], isProperty: true });
				} else {
					onChange({ isProperty: true });
				}
				return next;
			});
		},
		[isSuperAdmin, onChange]
	);

	const clearAll = useCallback(() => {
		setSelectedIds([]);
		onChange({});
	}, [onChange]);

	const nonAdminTenantId = isSuperAdmin
		? null
		: systemsStore.selectedTenant.value;
	const nonAdminProperties = nonAdminTenantId
		? (propertiesByParent[nonAdminTenantId] ?? [])
		: [];

	return {
		isSuperAdmin,
		open,
		loadingRoot,
		loadingParentId,
		tenants,
		propertiesByParent,
		selectedIds,
		expandedTenants,
		nonAdminProperties,
		handleOpenChange,
		toggleTenant,
		toggleCheck,
		clearAll
	};
}
